import { useState, useCallback } from "react";
// import apiClient from "../config/api";
import type { NameSuggestion, DjangoSimilarStyleNamesData } from "../types";
import { useGeneralApiCall } from "@/services/useGeneralApiCall";

interface UseStyleNameSuggestionsReturn {
  suggestions: NameSuggestion[];
  isLoading: boolean;
  fetchSuggestions: (searchTerm: string) => Promise<void>;
  clearSuggestions: () => void;
}

export const useStyleNameSuggestions = (): UseStyleNameSuggestionsReturn => {
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { getApi } = useGeneralApiCall();

  const fetchSuggestions = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);
      // Use Django API endpoint for similar style names
      //   const response = await apiClient.get<DjangoSimilarStyleNamesData>(
      //     `/style/v1/get-similar-style-names/${encodeURIComponent(searchTerm)}/`
      //   );

      const response = await getApi<DjangoSimilarStyleNamesData>(
        `api/style/v1/get-similar-style-names/${encodeURIComponent(searchTerm)}/`
      );

      console.log("Suggestions API response:", response.data); // Debug log

      // Handle different possible response structures
      let similarNames: string[] = [];
      const responseData = response.data;

      if (
        responseData.similar_names &&
        Array.isArray(responseData.similar_names)
      ) {
        similarNames = responseData.similar_names;
      } else if (responseData.results && Array.isArray(responseData.results)) {
        similarNames = responseData.results;
      } else if (responseData.names && Array.isArray(responseData.names)) {
        similarNames = responseData.names;
      } else if (responseData.data && Array.isArray(responseData.data)) {
        similarNames = responseData.data;
      } else if (Array.isArray(responseData)) {
        // In case the response is directly an array
        similarNames = responseData as unknown as string[];
      } else {
        console.warn("Unexpected response structure:", responseData);
        similarNames = [];
      }

      // Transform to NameSuggestion format
      const suggestions: NameSuggestion[] = similarNames.map((name, index) => ({
        id: `suggestion-${index}`,
        name,
        similarity: 0.8 - index * 0.1, // Approximate similarity based on order
      }));

      console.log("Setting suggestions:", suggestions); // Debug log
      setSuggestions(suggestions.slice(0, 3)); // Limit to 3 as specified
    } catch (error) {
      console.error("Error fetching name suggestions:", error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    isLoading,
    fetchSuggestions,
    clearSuggestions,
  };
};
